import { useTenantCohortsForVariables } from '@/hooks/queries/cohorts';
import {
  useCreateCohortVariable,
  usePatchCohortVariable,
} from '@/hooks/queries/variables/cohort-variables';
import { Cohort } from '@/types/cohort';
import type { CohortVariableValue, Variable } from '@/types/variables';
import { buildCohortValueMap, COHORT_VARIABLE_TYPE } from '@/types/variables';
import {
  stripCohortVariablePrefix,
  validateCohortVariableNameUnique,
  validateTenantVariableTitle,
} from '@/utils/promptUtils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from '@iconify/react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ScrollShadow,
} from '@nextui-org/react';
import '@uiw/react-markdown-preview/markdown.css';
import '@uiw/react-md-editor/markdown-editor.css';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const MDEditor = lazy(() => import('@uiw/react-md-editor'));

const bulletList = {
  name: 'bulletList',
  keyCommand: 'bulletList',
  buttonProps: { 'aria-label': 'Add bullet list' },
  icon: (
    <svg viewBox="0 0 16 16" width="12px" height="12px">
      <path
        fill="currentColor"
        d="M2 4a1 1 0 100-2 1 1 0 000 2zm3.75-1.5a.75.75 0 000 1.5h8.5a.75.75 0 000-1.5h-8.5zm0 5a.75.75 0 000 1.5h8.5a.75.75 0 000-1.5h-8.5zm0 5a.75.75 0 000 1.5h8.5a.75.75 0 000-1.5h-8.5zM3 8a1 1 0 11-2 0 1 1 0 012 0zm-1 6a1 1 0 100-2 1 1 0 000 2z"
      />
    </svg>
  ),
  execute: (state: any, api: any) => {
    const modifyText = `\n- ${state.selectedText || 'List item'}\n`;
    api.replaceSelection(modifyText);
  },
};

const numberedList = {
  name: 'numberedList',
  keyCommand: 'numberedList',
  buttonProps: { 'aria-label': 'Add numbered list' },
  icon: (
    <svg viewBox="0 0 16 16" width="12px" height="12px">
      <path
        fill="currentColor"
        d="M2.003 2.5a.5.5 0 00-.723-.447l-1.003.5a.5.5 0 00.446.895l.28-.14V6H.5a.5.5 0 000 1h2.006a.5.5 0 100-1h-.503V2.5zM5 3.25a.75.75 0 01.75-.75h8.5a.75.75 0 010 1.5h-8.5A.75.75 0 015 3.25zm0 5a.75.75 0 01.75-.75h8.5a.75.75 0 010 1.5h-8.5A.75.75 0 015 8.25zm0 5a.75.75 0 01.75-.75h8.5a.75.75 0 010 1.5h-8.5a.75.75 0 01-.75-.75zM.924 10.32l.003-.004a.851.851 0 01.144-.153A.66.66 0 011.5 10c.195 0 .306.068.374.146a.57.57 0 01.128.376c0 .453-.269.682-.8 1.078l-.035.025C.692 11.98 0 12.495 0 13.5a.5.5 0 00.5.5h2.003a.5.5 0 000-1H1.146c.132-.197.351-.372.654-.597l.047-.035c.47-.35 1.156-.858 1.156-1.845 0-.365-.118-.744-.377-1.038-.268-.303-.658-.484-1.126-.484-.48 0-.84.202-1.068.392a1.858 1.858 0 00-.348.384l-.007.011a.5.5 0 00.851.525zM.5 10.055l-.427-.26.427.26z"
      />
    </svg>
  ),
  execute: (state: any, api: any) => {
    const modifyText = `\n1. ${state.selectedText || 'List item'}\n`;
    api.replaceSelection(modifyText);
  },
};

const createCohortVariableSchema = (
  existingVariableNames: string[],
  editingName?: string | null
) =>
  z.object({
    name: z
      .string()
      .min(1, 'Variable name is required')
      .superRefine((name, ctx) => {
        const validation = validateTenantVariableTitle(name);
        if (!validation.isValid && validation.errorMessage) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: validation.errorMessage,
          });
          return;
        }

        const uniqueValidation = validateCohortVariableNameUnique(
          name,
          existingVariableNames,
          editingName
        );
        if (!uniqueValidation.isValid && uniqueValidation.errorMessage) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: uniqueValidation.errorMessage,
          });
        }
      }),
  });

type CohortVariableFormData = z.infer<
  ReturnType<typeof createCohortVariableSchema>
>;

interface AddCohortVariablePanelProps {
  isOpen: boolean;
  onClose: () => void;
  editingVariable?: Variable | null;
  existingVariableNames?: string[];
  onSuccess?: () => void;
}

export function AddCohortVariablePanel({
  isOpen,
  onClose,
  editingVariable,
  existingVariableNames = [],
  onSuccess,
}: AddCohortVariablePanelProps) {
  const { data: cohortsData } = useTenantCohortsForVariables({ enabled: isOpen });
  const createCohortVariable = useCreateCohortVariable();
  const patchCohortVariable = usePatchCohortVariable();
  const [cohortValues, setCohortValues] = useState<Record<number, string>>({});
  const [cohortValueErrors, setCohortValueErrors] = useState<
    Record<number, string>
  >({});
  const [valueEditorKey, setValueEditorKey] = useState(0);

  const cohorts = cohortsData?.results ?? [];
  const isEditing = Boolean(editingVariable?.id);
  const isSaving = createCohortVariable.isPending || patchCohortVariable.isPending;
  const editingName = editingVariable?.name ?? null;

  const cohortVariableSchema = useMemo(
    () => createCohortVariableSchema(existingVariableNames, editingName),
    [existingVariableNames, editingName]
  );

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CohortVariableFormData>({
    resolver: zodResolver(cohortVariableSchema),
    defaultValues: { name: '' },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const variableName = watch('name');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (editingVariable) {
      const valueMap = buildCohortValueMap(editingVariable.values);
      const nextValues: Record<number, string> = {};
      cohorts.forEach((cohort) => {
        const raw = valueMap.get(cohort.id);
        nextValues[cohort.id] =
          raw === null || raw === undefined ? '' : String(raw);
      });

      reset({ name: stripCohortVariablePrefix(editingVariable.name) });
      setCohortValues(nextValues);
      setCohortValueErrors({});
      return;
    }

    reset({ name: '' });
    setCohortValues({});
    setCohortValueErrors({});
    setValueEditorKey((key) => key + 1);
  }, [cohorts, editingVariable, isOpen, reset]);

  const validateCohortValues = () => {
    const nextErrors: Record<number, string> = {};
    let hasAtLeastOneValue = false;

    cohorts.forEach((cohort) => {
      const value = (cohortValues[cohort.id] ?? '').trim();
      if (value) {
        hasAtLeastOneValue = true;
      }
    });

    if (!hasAtLeastOneValue) {
      cohorts.forEach((cohort) => {
        nextErrors[cohort.id] = 'Variable value is required';
      });
    }

    setCohortValueErrors(nextErrors);
    return hasAtLeastOneValue;
  };

  const onSubmit = async (formData: CohortVariableFormData) => {
    if (!validateCohortValues()) {
      return;
    }

    const values: CohortVariableValue[] = cohorts.map((cohort) => {
      const trimmed = (cohortValues[cohort.id] ?? '').trim();
      return {
        cohort_id: cohort.id,
        value: trimmed || null,
      };
    });

    const variableName = stripCohortVariablePrefix(formData.name);

    if (isEditing && editingVariable) {
      await patchCohortVariable.mutateAsync({
        id: editingVariable.id,
        data: {
          name: variableName,
          values,
        },
      });
      onSuccess?.();
      onClose();
      return;
    }

    await createCohortVariable.mutateAsync({
      content_type: 'orgs.org',
      variables: {
        name: variableName,
        type: COHORT_VARIABLE_TYPE,
        description: '',
        props: { source: 'cohort' },
        required: false,
        nullable: true,
        visible: true,
        values: values.filter((entry) => entry.value !== null && entry.value !== ''),
      },
    });
    onSuccess?.();
    onClose();
  };

  const renderCohortValueEditor = (cohort: Cohort) => {
    const error = cohortValueErrors[cohort.id];

    return (
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={`cohort-variable-value-${cohort.id}`}
          className="text-foreground text-small font-medium"
        >
          {cohort.name} Value
        </label>
        <div
          data-color-mode="light"
          id={`cohort-variable-value-${cohort.id}`}
          className={`rounded-medium overflow-hidden ${
            error
              ? 'ring-.5 ring-danger border border-danger'
              : 'border border-default-200'
          }`}
        >
          <Suspense fallback={<div>Loading editor...</div>}>
            <MDEditor
              key={
                editingVariable
                  ? `edit-${editingVariable.id}-${cohort.id}`
                  : `add-${valueEditorKey}-${cohort.id}`
              }
              value={cohortValues[cohort.id] ?? ''}
              onChange={(value?: string) => {
                setCohortValues((current) => ({
                  ...current,
                  [cohort.id]: value || '',
                }));
                if (cohortValueErrors[cohort.id]) {
                  setCohortValueErrors((current) => {
                    const next = { ...current };
                    delete next[cohort.id];
                    return next;
                  });
                }
              }}
              preview="edit"
              height={200}
              textareaProps={{
                placeholder:
                  'Choose list type from toolbar: Bullet (•) or Numbered (1.)',
                'aria-label': `${cohort.name} variable value editor`,
              }}
              commands={[bulletList, numberedList]}
              extraCommands={[]}
            />
          </Suspense>
        </div>
        {error && <div className="text-danger text-xs">{error}</div>}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton
      classNames={{
        wrapper: '!items-start !justify-end',
        base: '!m-0 rounded-medium h-[calc(100vh-64px)]',
      }}
      size="md"
      motionProps={{
        variants: {
          enter: {
            x: 0,
            transition: { duration: 0.3 },
          },
          exit: {
            x: 800,
            transition: {
              duration: 0.2,
              ease: 'easeIn',
            },
          },
        },
      }}
    >
      <ModalContent className="absolute top-2 right-2 h-[calc(100vh-20px)]">
        {(modalClose) => (
          <>
            <ModalHeader className="flex justify-between items-center px-6 py-2 border-b">
              <span className="text-large font-semibold">
                {isEditing ? 'Edit Cohort Variable' : 'Add Cohort Variable'}
              </span>
              <Button isIconOnly variant="light" onPress={modalClose}>
                <Icon icon="mdi:close" className="h-5 w-5" />
              </Button>
            </ModalHeader>

            <ModalBody className="overflow-hidden mt-2">
              <ScrollShadow className="h-[calc(100vh-250px)]">
                <form
                  id="add-cohort-variable-form"
                  onSubmit={handleSubmit(onSubmit)}
                  className="flex flex-col space-y-6 px-1"
                >
                  <Input
                    label="Variable Name"
                    value={variableName || ''}
                    onChange={(event) =>
                      setValue('name', event.target.value, {
                        shouldValidate: true,
                      })
                    }
                    isInvalid={Boolean(errors.name)}
                    errorMessage={errors.name?.message}
                    description="No spaces or dots. Use underscores for multiple words (e.g. company_name, support_email)."
                    startContent={
                      <span className="text-default-400 text-sm pointer-events-none select-none">
                        cohort.
                      </span>
                    }
                    classNames={{
                      description: 'text-gray-500 text-xs leading-relaxed',
                    }}
                  />

                  {cohorts.length === 0 ? (
                    <div className="text-sm text-foreground-400">
                      Loading cohorts...
                    </div>
                  ) : (
                    cohorts.map((cohort) => (
                      <div key={cohort.id}>{renderCohortValueEditor(cohort)}</div>
                    ))
                  )}
                </form>
              </ScrollShadow>
            </ModalBody>

            <ModalFooter className="justify-between border-t mt-4">
              <Button variant="bordered" onPress={modalClose}>
                Cancel
              </Button>
              <Button
                color="primary"
                isLoading={isSaving}
                isDisabled={cohorts.length === 0}
                onPress={() => {
                  void handleSubmit(onSubmit)();
                }}
              >
                {isSaving
                  ? 'Saving...'
                  : isEditing
                    ? 'Save changes'
                    : 'Create variable'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
